import type { AsyncActionAPI } from 'src/replicant/config/actions/createSharedStatesActions';
import {
  canParticipateOnTeamCompetition,
  getTeamEventAnalytics,
} from './teamCompetition.getters';
import {
  getCurrentTeamEventId,
  getTeamBracket,
  getTeamEventScore,
} from '../team.getters';
import { MAX_TEAMS_PER_COMPETITION } from './teamCompetition.ruleset';

/**
 * Join team competition event.
 * @param teamId
 * @param rawApi
 * @returns Analytics data.
 */
export async function joinTeamCompetitionEvent(
  teamId: string,
  api: AsyncActionAPI,
): Promise<
  | false
  | {
      teamId: string;
      tournamentUniqueId: string;
      opponentCount: number;
      memberCount: number;
      avgOpponentSize: number;
    }
> {
  // Fetch team state.
  const teamState = await api.sharedStates.team.fetch(teamId);
  if (!teamState) {
    throw new Error('Cannot join team event, team state is undefined.');
  }

  // Check if team can participate on team competition.
  if (!canParticipateOnTeamCompetition(teamState)) {
    throw new Error('Team cannot participate on team competition.');
  }

  // No event to join.
  const now = api.date.now();
  const eventId = getCurrentTeamEventId(now);
  if (!eventId) return false;

  // Already joined the event.
  if (teamState.global.events[eventId]) return false;

  // Notify the team in chat.
  api.sharedStates.team.postMessage.sendChatMessageToTeam(teamId, {
    timestamp: now,
    contents: {
      type: 'event',
    },
  });

  // Check if team already belongs to an event. Only one team from the bracket will run matchmaking. The rest just need to join the event.
  const teamEvent = await api.sharedStates.teamCompetition.search({
    where: {
      teams: {
        teamId: {
          isAnyOf: [teamId],
        },
      },
    },
    limit: 1,
  });

  let competitionId = teamEvent.results[0]?.id;
  const eventType = 'competition';

  // If team already belongs to an event, just update team state to reflect that.
  if (teamEvent.results.length > 0) {
    api.sharedStates.team.postMessage.createEvent(teamId, {
      timestamp: now,
      eventId,
      sharedStateId: competitionId,
      eventType,
    });

    return getTeamEventAnalytics(
      teamId,
      competitionId,
      teamState,
      teamEvent.results[0].teams.map((x) => x.teamId),
      api,
    );
  }

  // Team does not belong to an event. Run matchmaking.

  // Figure out matchmaking bracket.
  const bracket = getTeamBracket(teamState, now);

  // For active and low activity the matchmaking is the same, the only difference is the size of the sub brackets.
  if (bracket === 'active' || bracket === 'lowActivity') {
    // Max score.
    const maxScore = (
      await api.sharedStates.team.search({
        where: {
          matchMakingBracket: {
            isAnyOf: [bracket],
          },
        },
        sort: [
          { field: 'previousEventScore', order: 'desc' },
          { field: 'createdTimestamp', order: 'desc' },
        ],
        limit: 1,
      })
    ).results[0]?.previousEventScore;

    // Min score.
    const minScore = (
      await api.sharedStates.team.search({
        where: {
          matchMakingBracket: {
            isAnyOf: ['active'],
          },
        },
        sort: [
          { field: 'previousEventScore', order: 'asc' },
          { field: 'createdTimestamp', order: 'asc' },
        ],
        limit: 1,
      })
    ).results[0]?.previousEventScore;

    // Amount of teams in the bracket.
    const teamsCount = await api.sharedStates.team.count({
      where: {
        matchMakingBracket: {
          isAnyOf: [bracket],
        },
      },
    });

    // Figure out which sub bracket to join.
    const bracketSize = bracket === 'active' ? 25 : 15;
    // Divide the bracket into sub brackets of size defined above.
    const totalSubBrackets = Math.floor(teamsCount / bracketSize);
    const bracketIncrement = (maxScore - minScore) / totalSubBrackets;
    // Figure out which sub bracket the team belongs to.
    const previousTeamScore = getTeamEventScore(teamState, eventId);
    const subBracket = Math.floor(
      (previousTeamScore - minScore) / bracketIncrement,
    );

    // Get all teams in the sub bracket.
    const teamIds = (
      await api.sharedStates.team.search({
        where: {
          matchMakingBracket: {
            isAnyOf: [bracket],
          },
          previousEventScore: {
            greaterThanOrEqual: minScore + subBracket * bracketIncrement,
            lessThan: minScore + (subBracket + 1) * bracketIncrement,
          },
        },
        sort: [
          { field: 'previousEventScore', order: 'desc' },
          { field: 'createdTimestamp', order: 'desc' },
        ],
        limit: bracketSize,
      })
    ).results.map((r) => r.id);

    // Create competition.
    competitionId = api.sharedStates.teamCompetition.create();

    // Set teams.
    api.sharedStates.teamCompetition.postMessage.setTeams(competitionId, {
      timestamp: now,
      teamIds,
    });

    // Join competition.
    api.sharedStates.team.postMessage.createEvent(teamId, {
      timestamp: now,
      eventId,
      sharedStateId: competitionId,
      eventType,
    });

    return getTeamEventAnalytics(
      teamId,
      competitionId,
      teamState,
      teamIds,
      api,
    );
  }

  // For new teams, we either join an existing competition or create a new one based on how many competitions are running.
  const competitionsCount = await api.sharedStates.teamCompetition.search({
    where: {
      teamsCount: {
        lessThan: MAX_TEAMS_PER_COMPETITION,
      },
    },
    limit: 5,
    sort: [{ field: 'teamsCount', order: 'asc' }],
  });

  // Not enough competitions running.
  const minCompetitions = process.env.IS_DEVELOPMENT ? 1 : 5;
  if (competitionsCount.results.length < minCompetitions) {
    // Create competition.
    competitionId = api.sharedStates.teamCompetition.create();
    api.sharedStates.teamCompetition.postMessage.setTeams(competitionId, {
      timestamp: now,
      teamIds: [teamId],
    });

    // Update team state.
    api.sharedStates.team.postMessage.createEvent(teamId, {
      timestamp: now,
      eventId,
      sharedStateId: competitionId,
      eventType,
    });

    return getTeamEventAnalytics(teamId, competitionId, teamState, [], api);
  }

  // Join existing competition.
  const competition = competitionsCount.results[0];
  competitionId = competition.id;
  api.sharedStates.teamCompetition.postMessage.join(competitionId, {
    teamId,
    timestamp: now,
  });

  // Update team state.
  api.sharedStates.team.postMessage.createEvent(teamId, {
    timestamp: now,
    eventId,
    sharedStateId: competitionId,
    eventType,
  });

  return getTeamEventAnalytics(
    teamId,
    competitionId,
    teamState,
    competition.teams.map((x) => x.teamId),
    api,
  );
}
